import React from "react";
import { Flex, Box, Text, AspectRatio } from "@chakra-ui/react";
import { sectionSpacingY } from "components/section";
import { useColorThemeToggle } from "hooks/use-color-theme-toggle";
import { horizontalContainerPadding } from "ui/container";
import { FillImage } from "components/images";
import image1 from "../../../../public/assets/platform/agents/how-it-works-1.png";
import image2 from "../../../../public/assets/platform/agents/how-it-works-2.png";
import image3 from "../../../../public/assets/platform/agents/how-it-works-3.png";

const diptychConfig = [
  {
    title: "Complete context",
    description: (
      <>
        <Text>Hightouch Agents are powered by the same data that drives your business.</Text>
        <br />
        <Text>
          They connect directly to your data warehouse and marketing channels, ensuring every analysis and
          recommendation reflects real, up-to-date information.
        </Text>
      </>
    ),
    media: (
      <FillImage
        src={image1}
        alt="Diagram showing Hightouch Agents' capability to centralize data and build knowledge."
      />
    ),
  },
  {
    title: "Expert-level marketing skill",
    description: (
      <>
        <Text>Hightouch AI Agents don&apos;t just understand data, they understand marketing.</Text>
        <br />
        <Text>
          Preloaded with deep domain knowledge, they can reason about complex concepts like creative fatigue,
          attribution modeling, and incrementality with expert precision.
        </Text>
      </>
    ),
    media: (
      <FillImage
        src={image2}
        alt="Diagram illustrating the analytical layer of Hightouch Agents. The central Agent processes data to enable key functions: 'Attribution,' 'Creative analysis,' and 'Predictive ML."
      />
    ),
  },
  {
    title: "Take action with your agents",
    description: (
      <>
        <Text>Hightouch AI Agents don&apos;t stop at recommendations—they act on them.</Text>
        <br />
        <Text>
          Connected to all your marketing and business systems, they can build, launch, manage, and optimize campaigns
          across hundreds of marketing channels in real-time.
        </Text>
      </>
    ),
    media: <FillImage src={image3} alt="Diagram showcasing the execution layer of Hightouch Agents." />,
  },
];

export function AgentsHowItWorks() {
  const scope = useColorThemeToggle({ theme: "light", bgColor: "new.blue.50" });

  return (
    <Box
      ref={scope}
      id="how-it-works"
      as="section"
      w="100%"
      pos="relative"
      overflow="hidden"
      borderTopRadius={{ base: "big", sm: "bigger", md: "biggest" }}
      bg="new.blue.50"
      py={{ base: "biggest", lg: "hugest" }}
      px={horizontalContainerPadding}
    >
      <Box pos="relative" w="100%" maxW="container.section" mx="auto">
        <Flex
          w="100%"
          flexDir="column"
          align={{ base: "start", md: "center" }}
          textAlign={{ base: "start", md: "center" }}
          gap="smaller"
          maxWidth="2xl"
          mx={{ base: "0", md: "auto" }}
        >
          <Text
            as="h3"
            fontSize={{ base: "24px", md: "40px" }}
            fontWeight="bold"
            lineHeight={{ base: 1.3, md: 1.2 }}
            whiteSpace="pre-wrap"
          >
            Agents purpose-built with marketing skill and context
          </Text>

          <Text align={{ base: "start", md: "center" }} fontSize={{ base: "16px", md: "18px" }} lineHeight={1.5}>
            Hightouch Agents are pre-trained for marketing and connect to all your data, channels, and creative.
            Incredibly useful, without endless prompting.
          </Text>
        </Flex>

        <Flex flexDir="column" gap={sectionSpacingY} mt={{ base: "biggest", md: "huger" }}>
          {diptychConfig.map((item, index) => {
            const direction = index % 2 === 0 ? "right" : "left";
            const mediaRatio = 626 / 430;

            return (
              <Flex
                key={item.title}
                align="center"
                flexDir={{ base: "column", md: direction === "right" ? "row" : "row-reverse" }}
                gap={{ base: "small", md: "big", lg: "huge" }}
                justifyContent={{ base: "center", md: "space-between" }}
                maxW={{ base: "100%", lg: "90%" }}
                mx="auto"
                w="100%"
              >
                <Flex direction="column" mx="auto" flex={1} gap="smaller">
                  <Text as="h3" fontSize={{ base: "20px", md: "24px" }} fontWeight="bold" lineHeight={1.2}>
                    {item.title}
                  </Text>

                  <Text as="div" fontSize={{ base: "14px", md: "16px" }} lineHeight={1.4}>
                    {item.description}
                  </Text>
                </Flex>

                <AspectRatio
                  ratio={mediaRatio}
                  overflow="hidden"
                  borderRadius={{ base: "small", md: "bigger" }}
                  border="none"
                  flex={1}
                  w={{ base: "100%", md: "50%" }}
                  maxW={{ base: "mediaMaxW.mobile", md: "100%", lg: "626px" }}
                  pos="relative"
                >
                  {item.media}
                </AspectRatio>
              </Flex>
            );
          })}
        </Flex>
      </Box>
    </Box>
  );
}
